import React from 'react';
import { View } from 'react-native';

import BookButton from '../BookButton';
import ServiceInfo from './ServiceInfo';
import SeeReviewsLink from '../seeReviews/SeeReviewsLink';
import { IServiceSection, SubsetOfFieldsFromFormattedService } from '../../../../modules/Api.types';
import { PublicProvider } from '../../../../api/Providers';

import './NormalServiceItem.module.scss';
import { TopServiceTagHandlerABTestCOM1252 } from './TopServiceTagHandlerABTestCOM1252';

/**
 * Renders the "default" style of a service item, with service details like description and price
 * range
 */
const NormalServiceItem = ({
  appearance = 'default',
  service,
  isDisabled,
  isEditMode,
  formattedDiscount,
  allowOnlineBooking,
  providerId,
  providerPhone,
  index,
  group,
  showAndUp,
  description,
  isExpanded,
  onInfoClick,
  bookText = 'See Times',
  seeReviewsProps,
  useShortDescription,
  additionalTracking,
  highlightedServiceId = null,
  hideTags,
}: {
  appearance: 'default' | 'notifyMe';
  service: SubsetOfFieldsFromFormattedService<'id' | 'name' | 'cost' | 'total_duration_minutes' | 'isHighlight' | 'is_online_bookable' | 'tags'> | null;
  isDisabled?: boolean;
  isEditMode?: boolean;
  formattedDiscount?: string;
  allowOnlineBooking?: boolean;
  providerId: number;
  providerPhone?: string;
  index: number;
  group: IServiceSection;
  showAndUp?: boolean;
  description?: string;
  isExpanded?: boolean;
  onInfoClick?: () => void;
  bookText?: string;
  seeReviewsProps?: {
    salon: Pick<PublicProvider, 'id'> | null;
    provider: Pick<PublicProvider, 'id' | 'locations' | 'smart_pricing_enabled'> | null;
    service: SubsetOfFieldsFromFormattedService<'id' | 'cost_and_up'> | null;
  };
  useShortDescription?: boolean;
  additionalTracking?: any;
  highlightedServiceId?: number;
  hideTags?: boolean;
}) => {
  // Simplified service only shows the name, price, time, and renderRight
  const showSimplifiedService = appearance === 'notifyMe';
  const MAX_LENGTH = 100;
  const allowSeeReviews = seeReviewsProps !== null;
  const showExpandButton = (
    !useShortDescription
    && allowSeeReviews
    && description.length > MAX_LENGTH
  );
  const isOnlineBookable = allowOnlineBooking && service.is_online_bookable;

  const renderSeeReviewsLink = () => (
    <React.Fragment>
      <TopServiceTagHandlerABTestCOM1252 service={service} />
      <SeeReviewsLink {...seeReviewsProps} isEditMode={isEditMode} />
    </React.Fragment>
  );

  return (
    <View styleName="serviceContainer" testID="serviceItem">
      <View
        styleName="serviceInfo"
        testID="serviceInfo"
      >
        <ServiceInfo
          formattedDiscount={formattedDiscount}
          service={service}
          hideTags={hideTags}
          showAndUp={showAndUp}
          isExpanded={isExpanded}
          description={!showSimplifiedService && description}
          onInfoClick={onInfoClick}
          showExpandButton={showExpandButton}
          useShortDescription={useShortDescription}
          showFullDescription={false}
          renderSeeReviewsLink={showSimplifiedService ? () => null : renderSeeReviewsLink}
        />
      </View>
      {!isDisabled && (
        <View
          styleName="bookButtonContainer"
          testID="bookButtonContainer"
        >
          <BookButton
            isEditMode={isEditMode}
            isOnlineBookable={isOnlineBookable}
            service={service}
            providerId={providerId}
            providerPhone={providerPhone}
            index={index}
            group={group}
            bookText={bookText}
            additionalTracking={additionalTracking}
            highlightedServiceId={highlightedServiceId}
          />
        </View>
      )}
    </View>
  );
};

NormalServiceItem.defaultProps = {
  isDisabled: false,
  isEditMode: false,
  allowOnlineBooking: false,
  formattedDiscount: null,
  providerPhone: null,
  isExpanded: false,
  showAndUp: false,
  description: null,
  onInfoClick: null,
  seeReviewsProps: null,
  useShortDescription: false,
  additionalTracking: null,
};

export default NormalServiceItem;
